/* rein-swipe-marquee.js — 2026-09-27
 *
 * One engine for every auto-scrolling row on the site (press ticker, Shop by
 * System Type, Our Most Popular Systems, the review wall's two rows).
 *
 * WHY: Microsoft Clarity showed phone visitors trying to thumb-swipe the
 * homepage rows and nothing happening. The rows were CSS keyframe marquees
 * (transform: translate3d(-50%)) inside overflow:hidden viewports, so touch
 * had nothing to scroll. Every row is now a NATIVE horizontal scroller
 * (overflow-x:auto, momentum scrolling, hidden scrollbar) and the auto-glide
 * is a requestAnimationFrame loop that advances scrollLeft. A native scroller
 * gives thumb swipe, momentum, and the browser's own tap-vs-drag distinction
 * for free: a swipe never fires a click, and a tap on a stationary card is an
 * ordinary click.
 *
 * MARKUP CONTRACT (set by each section):
 *   viewport  [data-rein-mq]            the element that scrolls
 *     data-rein-mq-copies="3"           how many identical copies the rail holds
 *     data-rein-mq-speed="shared"|<px/s> "shared" = SHARED_SPEED below (all card rows)
 *     data-rein-mq-duration="<s>"       ticker only: seconds for ONE copy to pass
 *                                       (speed = copy width / duration, so the
 *                                       authored pace survives item-count changes)
 *     data-rein-mq-dir="reverse"        content moves to the right instead of left
 *     data-rein-mq-snap="cards"|"none"  idle-snap to card starts (cards) or free (ticker)
 *     data-rein-mq-cell="<selector>"    one card, for the snap/arrow step size
 *     data-rein-mq-delay="<ms>"         wait this long after first coming into
 *                                       view before the glide starts
 *   rail      [data-rein-mq-rail]       flex row, width:max-content, children = copies
 *     data-rein-mq-glide                (HOME-3) always auto-glide, even with FORCE_STATIC
 *                                       and on touch; only prefers-reduced-motion stops it
 *   dup items [data-rein-mq-dup]        the duplicate copies (aria-hidden + inert in
 *                                       the markup; hidden entirely in static mode)
 *     data-rein-mq-static               (2026-09-27b) plain swipeable row: no glide,
 *                                       one copy, no wrap; arrows scroll one cell.
 *                                       copies="1" means the same thing.
 *   arrows    [data-rein-mq-prev] / [data-rein-mq-next] inside the same section
 *
 * INFINITE LOOP: the rail holds >= 3 identical copies. scrollLeft is kept
 * inside [0.5 * copy, 1.5 * copy): crossing either edge jumps by exactly one
 * copy width, which lands on pixel-identical content, so the wrap is
 * invisible. There is always at least half a copy of slack on both sides, so
 * a swipe in either direction has somewhere to go. During the auto-glide the
 * wrap happens inline; during a user gesture it waits for the scroll to go
 * idle (setting scrollLeft mid-momentum can kill the fling on iOS).
 *
 * SNAP: CSS scroll-snap is NOT used while the glide can run, because Chrome
 * re-snaps on every programmatic scrollLeft assignment and the glide would
 * stutter from card to card. Instead, once a card row goes idle after a
 * gesture, it eases to the nearest card start (scrollTo smooth). In static
 * mode (prefers-reduced-motion) there is no glide, so real CSS
 * scroll-snap-type is switched on via .rein-mq--static.
 *
 * PAUSE/RESUME: pauses the instant a finger lands (touchstart), a mouse enters
 * (pointerenter, mouse only), a wheel/trackpad scrolls, an arrow is used, or
 * KEYBOARD focus moves inside (:focus-visible — a mouse click never holds, HOME-3c);
 * the cursor leaving resumes at once, everything else resumes RESUME_MS after
 * the LAST interaction ends.
 * Only runs while in view and the tab is visible. prefers-reduced-motion:
 * no glide at all, still swipeable, duplicates hidden.
 *
 * CLONES ARE NOT INERT (hotfix 2026-09-27c): the sections mark copies B/C
 * aria-hidden + inert so that without JS the row is a plain overflow list whose
 * clones are invisible to keyboard and screen reader. But the engine parks
 * scrollLeft inside copy B (see INFINITE LOOP), so the cards on screen ARE the
 * clones, and an inert subtree is hit-test transparent: the browser dropped
 * every tap and click on them (live: "Shop by System Type" cards dead). The
 * engine therefore strips `inert` from every [data-rein-mq-dup] at init and
 * keeps the clones keyboard-inaccessible the explicit way: aria-hidden="true"
 * on the copy, tabindex="-1" on each focusable descendant. Originals are never
 * touched. No click handler exists here on purpose: the native scroller already
 * cancels the click after a real swipe and lets a stationary tap through.
 *
 * TOUCH DEVICES (2026-09-28, the owner): on a coarse-pointer / no-hover device
 * ('(hover: none) and (pointer: coarse)') a row moves ONLY by the user's thumb.
 * Phones showed the auto-glide and the idle-snap pulling a row back the other
 * way right after a swipe. Touch therefore takes the STATIC path: no glide, no
 * idle-snap, no programmatic scrollLeft writes at all (arrows, if a section
 * shows them, use scrollBy on the user's tap). The row is a plain native
 * scroller with CSS scroll-snap (x proximity, see the .css) and momentum, the
 * loop clones are display:none so the row has a natural start and end, and it
 * starts at its first card. Desktop / fine pointer is unchanged.
 */
(function () {
  'use strict';
  if (window.ReinMarquee) return;

  var FORCE_STATIC = true; // HOME-2 2026-10-21 (owner): no auto-glide anywhere (except rows with data-rein-mq-glide, HOME-3) — every other row is a static swipeable scroller with arrows
  var SHARED_SPEED = 72;   // px/s for every card row (= the fastest row measured live 2026-09-27)
  var RESUME_MS = 3000;    // resume this long after the last interaction
  var IDLE_MS = 160;       // scroll silence that counts as "gesture finished"
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var touchMQ = window.matchMedia('(hover: none) and (pointer: coarse)');

  function Marquee(vp) {
    var self = this;
    this.vp = vp;
    this.rail = vp.querySelector('[data-rein-mq-rail]');
    if (!this.rail) return;
    vp.__reinMq = this;
    var d = vp.dataset;
    this.copies = parseInt(d.reinMqCopies, 10) || 3;
    this.dir = d.reinMqDir === 'reverse' ? -1 : 1;
    this.delay = parseInt(d.reinMqDelay, 10) || 0;
    this.snapCards = d.reinMqSnap === 'cards';
    this.cellSel = d.reinMqCell || null;
    this.speed = (!d.reinMqSpeed || d.reinMqSpeed === 'shared') ? SHARED_SPEED : (parseFloat(d.reinMqSpeed) || SHARED_SPEED);
    this.duration = parseFloat(d.reinMqDuration) || 0;
    this.pos = 0; this.lastTs = 0; this.raf = null;
    this.holds = {}; this.resumeTimer = null; this.idleTimer = null; this.startTimer = null;
    this.inView = false; this.started = false; this.touching = false; this.auto = false;
    /* 2026-09-27b (collection redesign): STATIC rows. data-rein-mq-static (or
       copies="1") = a plain thumb-swipeable row with no glide, no duplicate
       copies and no wrap — the picks row, trust strip, type tiles and
       comparison tables. Same touch/momentum/arrow/snap behaviour as the
       reduced-motion path, which is exactly that mode. */
    this.forceStatic = d.reinMqStatic !== undefined || this.copies === 1;
    this.touch = touchMQ.matches;                 // thumb-only row, see TOUCH DEVICES above
    /* HOME-3 2026-10-21 (owner): data-rein-mq-glide = this row ALWAYS auto-glides (press ticker) — on touch too — and
       ignores FORCE_STATIC. Only prefers-reduced-motion still stops it. */
    this.glide = d.reinMqGlide !== undefined;
    this.static = reduceMQ.matches || (!this.glide && (FORCE_STATIC || this.forceStatic || this.touch));
    /* CONVERT-1101 2026-11-01: a glide row (the press ticker) is a CSS keyframe animation now, not a
       requestAnimationFrame loop writing scrollLeft 60× a second (3.6 s of main-thread time per home
       page load in Lighthouse). Same look: one copy width per data-rein-mq-duration seconds, linear,
       seamless wrap; paused while off screen (IntersectionObserver) or the tab is hidden, paused by
       the cursor / keyboard focus and resumed the moment it leaves (CSS :hover / :focus-within).
       prefers-reduced-motion keeps the static wrapped list exactly as before. */
    this.css = this.glide && !reduceMQ.matches;

    vp.classList.add('rein-mq');
    if (this.snapCards) vp.classList.add('rein-mq--cards');
    this.rail.classList.add('rein-mq__rail');
    if (this.static) vp.classList.add('rein-mq--static');
    if (this.touch) vp.classList.add('rein-mq--touch');

    this.unlockDups();
    if (this.css) { this.initCss(); return; }
    this.bind();
    if (!this.static) {
      this.centre();
      this.watchView();
    }
    var root = vp.closest('[id^="shopify-section-"]') || document;
    var prev = root.querySelectorAll('[data-rein-mq-prev]');
    var next = root.querySelectorAll('[data-rein-mq-next]');
    var onArrow = function (dir) { return function () { self.arrow(dir); }; };
    for (var i = 0; i < prev.length; i++) prev[i].addEventListener('click', onArrow(-1));
    for (var j = 0; j < next.length; j++) next[j].addEventListener('click', onArrow(1));
  }
  var P = Marquee.prototype;

  /* CONVERT-1101: CSS-animated glide row. The rail gets the keyframes from assets/rein-swipe-marquee.css
     (.rein-mq--css .rein-mq__rail), fed by two custom properties: --rein-mq-copies (translate by one copy =
     -100%/copies of the rail) and --rein-mq-duration (seconds for one copy to pass = data-rein-mq-duration,
     or copy width / px-per-second for speed rows). .rein-mq--run is the only thing JS toggles: on while the
     viewport intersects and the tab is visible, off otherwise, so an off-screen ticker costs nothing. */
  P.initCss = function () {
    var self = this, vp = this.vp;
    vp.classList.add('rein-mq--css');
    if (this.dir < 0) vp.classList.add('rein-mq--rev');
    var secs = this.duration > 0 ? this.duration : 0;
    if (!secs) { var p = this.period(); secs = p && this.speed ? p / this.speed : 20; }
    this.rail.style.setProperty('--rein-mq-copies', String(this.copies));
    this.rail.style.setProperty('--rein-mq-duration', secs + 's');
    var run = function () { vp.classList.toggle('rein-mq--run', self.inView && document.visibilityState !== 'hidden'); };
    var set = function (v) { if (v !== self.inView) { self.inView = v; run(); } };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { set(entries[0].isIntersecting); }, { threshold: 0 }).observe(vp);
    } else {
      set(true);
    }
    document.addEventListener('visibilitychange', run);
    this.started = true; this.auto = true;   // the HOME-3 checks read these
  };

  /* Make the duplicate copies clickable (see CLONES ARE NOT INERT above). */
  P.unlockDups = function () {
    var dups = this.vp.querySelectorAll('[data-rein-mq-dup]');
    for (var i = 0; i < dups.length; i++) {
      var d = dups[i];
      if (this.static) { d.parentNode.removeChild(d); continue; }   // static: clones leave the DOM entirely (HOME-2)
      d.removeAttribute('inert');
      d.setAttribute('aria-hidden', 'true');
      var f = d.querySelectorAll('a[href], area[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable]');
      for (var j = 0; j < f.length; j++) f[j].setAttribute('tabindex', '-1');
    }
  };
  P.period = function () { return this.copies ? this.rail.scrollWidth / this.copies : 0; };
  P.speedPx = function () {
    if (this.duration > 0) { var p = this.period(); return p ? p / this.duration : 0; }
    return this.speed;
  };
  P.cell = function () { return this.cellSel ? this.rail.querySelector(this.cellSel) : this.rail.firstElementChild; };
  P.step = function () {
    var c = this.cell();
    if (!c) return this.vp.clientWidth;
    var cs = getComputedStyle(c);
    var gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    return c.getBoundingClientRect().width + (parseFloat(cs.marginRight) || 0) + (parseFloat(cs.marginLeft) || 0) + gap;
  };
  P.wrap = function (x) {
    var p = this.period();
    if (!p) return x;
    if (x < 0.5 * p) x += p;
    else if (x >= 1.5 * p) x -= p;
    return x;
  };
  /* Start on copy B so there is a full copy of slack to the left from the first frame. */
  P.centre = function () {
    var p = this.period();
    if (p && this.vp.scrollLeft < 0.5 * p) { this.pos = p; this.vp.scrollLeft = p; }
  };
  P.canRun = function () {
    if (this.static || !this.started || !this.inView || this.resumeTimer) return false;
    for (var k in this.holds) if (this.holds[k]) return false;
    return document.visibilityState !== 'hidden';
  };
  P.sync = function () {
    var run = this.canRun();
    if (run && !this.auto) {
      this.auto = true; this.vp.classList.add('rein-mq--auto'); this.lastTs = 0; this.loop();
    } else if (!run && this.auto) {
      this.auto = false; this.vp.classList.remove('rein-mq--auto');
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    }
  };
  P.loop = function () {
    var self = this;
    if (this.raf) return;
    this.raf = requestAnimationFrame(function tick(ts) {
      self.raf = null;
      if (!self.auto) return;
      var dt = self.lastTs ? Math.min(0.05, (ts - self.lastTs) / 1000) : 0;
      self.lastTs = ts;
      var sl = self.vp.scrollLeft;
      if (Math.abs(sl - self.pos) > 1.5) self.pos = sl;          // adopt any outside change
      self.pos = self.wrap(self.pos + self.dir * self.speedPx() * dt);
      self.vp.scrollLeft = self.pos;
      self.raf = requestAnimationFrame(tick);
    });
  };
  P.hold = function (why) {
    this.holds[why] = true;
    clearTimeout(this.resumeTimer); this.resumeTimer = null;
    this.sync();
  };
  P.release = function (why) {
    delete this.holds[why];
    if (why === 'hover') { this.sync(); return; }   // HOME-3c: cursor off the row = play again right away
    this.scheduleResume();
  };
  /* HOME-3c: a focus hold only counts for KEYBOARD focus. A mouse click on an item focuses the <a> too, and that hold never
     cleared when the cursor left (the row stayed paused for good). */
  P.keyboardFocus = function () {
    var a = document.activeElement;
    if (!a || !this.vp.contains(a)) return false;
    try { return a.matches(':focus-visible'); } catch (e) { return true; }
  };
  /* One-shot interaction (wheel, arrow, an untracked scroll): pause now, resume RESUME_MS later. */
  P.poke = function () { this.scheduleResume(); };
  P.scheduleResume = function () {
    var self = this;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () { self.resumeTimer = null; self.sync(); }, RESUME_MS);
    this.sync();
  };
  P.onIdle = function () {
    if (this.touching || this.static) return;
    var x = this.vp.scrollLeft;
    var w = this.wrap(x);
    if (w !== x) { this.vp.scrollLeft = w; x = w; }
    this.pos = x;
    if (this.snapCards && !this.auto) {
      var s = this.step();
      if (s > 0) {
        var target = Math.round(x / s) * s;
        if (Math.abs(target - x) > 1) {
          this.pos = target;
          this.vp.scrollTo({ left: target, behavior: 'smooth' });
        }
      }
    }
  };
  P.arrow = function (dir) {
    this.poke();
    var s = this.step();
    if (this.static) { this.vp.scrollBy({ left: dir * s, behavior: 'smooth' }); return; }
    var target = Math.round(this.vp.scrollLeft / s) * s + dir * s;
    this.pos = target;
    this.vp.scrollTo({ left: target, behavior: 'smooth' });
  };
  P.bind = function () {
    var self = this, vp = this.vp;
    vp.addEventListener('touchstart', function () { self.touching = true; self.hold('touch'); }, { passive: true });
    var endTouch = function () { self.touching = false; self.release('touch'); self.armIdle(); };
    vp.addEventListener('touchend', endTouch, { passive: true });
    vp.addEventListener('touchcancel', endTouch, { passive: true });
    vp.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') self.hold('hover'); });
    vp.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      if (self.holds.focus && !self.keyboardFocus()) delete self.holds.focus;   // HOME-3c: a clicked link must not keep the row paused
      self.release('hover');
    });
    /* HOME-3c: belt and braces — if a pointerleave ever goes missing, the first mouse move outside the row releases the hover hold */
    document.addEventListener('pointermove', function (e) {
      if (self.holds.hover && e.pointerType === 'mouse' && !vp.contains(e.target)) {
        if (self.holds.focus && !self.keyboardFocus()) delete self.holds.focus;
        self.release('hover');
      }
    }, { passive: true });
    vp.addEventListener('wheel', function () { self.poke(); }, { passive: true });
    vp.addEventListener('focusin', function () { if (self.keyboardFocus()) self.hold('focus'); });
    vp.addEventListener('focusout', function (e) { if (!e.relatedTarget || !vp.contains(e.relatedTarget)) self.release('focus'); });
    vp.addEventListener('scroll', function () {
      if (self.auto) return;                        // our own frames
      self.pos = vp.scrollLeft;
      self.armIdle();
    }, { passive: true });
    document.addEventListener('visibilitychange', function () { self.sync(); });
    window.addEventListener('resize', function () { self.armIdle(); }, { passive: true });
  };
  P.armIdle = function () {
    var self = this;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(function () { self.onIdle(); }, IDLE_MS);
  };
  P.setInView = function (v) {
    var self = this;
    if (v === this.inView) return;
    this.inView = v;
    if (v) {
      this.centre();
      if (!this.started && !this.startTimer) {
        this.startTimer = setTimeout(function () { self.startTimer = null; self.started = true; self.sync(); }, this.delay);
      }
    } else if (!this.started && this.startTimer) {
      clearTimeout(this.startTimer); this.startTimer = null;   // left before the delay ran out: start over next time
    }
    this.sync();
  };
  P.watchView = function () {
    var self = this;
    var geom = function () {
      var r = self.vp.getBoundingClientRect();
      self.setInView(r.bottom > 0 && r.top < window.innerHeight && r.width > 0);
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { self.setInView(entries[0].isIntersecting); }, { threshold: 0.05 }).observe(this.vp);
    }
    /* Geometry fallback alongside the observer: IO does not fire reliably in
       every environment (this project's own automation tab, for one). */
    var t = null;
    var tick = function () { if (t) return; t = setTimeout(function () { t = null; geom(); }, 200); };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick, { passive: true });
    geom();
  };

  function scan(root) {
    var list = (root || document).querySelectorAll('[data-rein-mq]');
    for (var i = 0; i < list.length; i++) if (!list[i].__reinMq) new Marquee(list[i]);
  }
  window.ReinMarquee = { scan: scan, SHARED_SPEED: SHARED_SPEED, RESUME_MS: RESUME_MS, touch: touchMQ.matches };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
